import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { meetingsApi, downloadAuthed, wapsignApi, sortByAgendaOrder, type AgendaItemRow } from '../features/meetings/api.ts';
import {
  yaumiyyaApi, type Attendance, type NotesMap, type NoteEntry, type Proposal, type Details,
  type ObserverRow, type VoteChoice, type AttendanceMode,
} from '../features/yaumiyya/api.ts';
import { orgSettingsApi } from '../features/orgSettings/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { dv } from '../i18n/dv.ts';
import { formatDvDate, formatDvTime } from '../lib/dvDate.ts';
import { displayName } from '../lib/utils.ts';
import { writerRoles, electedRoles } from '../lib/body.ts';
import { talkingPointsApi } from '../features/talkingPoints/api.ts';
import { AutoGrowTextarea } from '../components/AutoGrowTextarea.tsx';
import { Icon } from '../components/Icon.tsx';
import { Dropdown } from '../components/Dropdown.tsx';
import { RecordingBar } from '../features/recording/RecordingBar.tsx';
import { NowPlayingBar } from '../features/recording/NowPlayingBar.tsx';
import { RecordingsCard } from '../features/recording/RecordingsCard.tsx';
import { listRecordings, deleteRecording } from '../features/recording/api.ts';

// Secretariat picker (ސެކްރެޓޭރިއެޓް) lists the SG + bureau staff.
const SECRETARIAT_ROLES = new Set(['SECRETARY_GENERAL', 'BUREAU_STAFF']);

const EMPTY_DETAILS: Details = {};

// Migrate stored notes to the multi-proposal shape on load. A note already
// carrying `proposals` is kept; a legacy single-proposal note is folded into one
// proposal (preserving its seconder) so the editor only ever deals in proposals.
function normalizeNotes(notes: NotesMap): NotesMap {
  const out: NotesMap = {};
  for (const [id, n] of Object.entries(notes)) {
    if (n.proposals && n.proposals.length > 0) {
      out[id] = { discussion: n.discussion, proposals: n.proposals };
      continue;
    }
    const hasProposal = !!(n.proposedBy?.trim() || n.secondedBy?.trim() || n.decision?.trim()
      || n.votes?.length || n.votesFor?.length || n.votesAgainst?.length);
    const p: Proposal = {};
    if (n.proposedBy) p.proposedBy = n.proposedBy;
    if (n.secondedBy) p.secondedBy = n.secondedBy;
    if (n.decision) p.decision = n.decision;
    if (n.votes) p.votes = n.votes;
    if (n.votesFor) p.votesFor = n.votesFor;
    if (n.votesAgainst) p.votesAgainst = n.votesAgainst;
    out[id] = { discussion: n.discussion, proposals: hasProposal ? [p] : [] };
  }
  return out;
}

// Small presentational helpers kept local to this file.
function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block space-y-1.5">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

// Shared form-control class. `.input` is intentionally reused for the editor's
// text inputs, time fields and textareas so they all share radius/focus styling.
const inputCls = 'input';

// Inline AI generate-discussion panel. Self-contained: owns draft/loading/error
// state so it doesn't pollute the editor. "Use" calls onUse (→ setNote) which
// writes into the editor's local state; the editor's existing autosave persists.
function GenerateDiscussion({ meetingId, itemId, onUse }: {
  meetingId: string; itemId: string; onUse: (text: string) => void;
}): JSX.Element {
  const { data: points = [] } = useQuery({
    queryKey: ['talkingPoints', meetingId, itemId],
    queryFn: () => talkingPointsApi.list(meetingId, itemId),
  });
  const hasPoints = points.length > 0;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  // One generation per page load: once it succeeds the generate button is gone,
  // so the SG can't keep re-rolling (and burning API calls). To get a fresh
  // draft they reload the page.
  const [generated, setGenerated] = useState(false);

  async function run(): Promise<void> {
    setLoading(true); setError(null); setOpen(true);
    try {
      const text = await yaumiyyaApi.generateDiscussion(meetingId, itemId);
      setDraft(text);
      setGenerated(true);
    } catch {
      setError(dv.yaumiyya.generateError);
    } finally { setLoading(false); }
  }

  return (
    <div className="space-y-2">
      {!generated && (
        <button type="button" className="btn-secondary btn-sm disabled:opacity-50"
          disabled={!hasPoints || loading} title={hasPoints ? '' : dv.yaumiyya.noTalkingPoints}
          onClick={() => void run()}>
          <Icon name="auto_awesome" size={16} /> {loading ? dv.yaumiyya.generating : dv.yaumiyya.generateDiscussion}
        </button>
      )}
      {open && (
        <div className="space-y-2 rounded-2xl bg-[hsl(var(--surface-container-low))] p-3">
          {error ? <p className="text-sm text-destructive">{error}</p> : (
            <AutoGrowTextarea className={`${inputCls} min-h-[9rem]`} value={draft} onChange={(e) => setDraft(e.target.value)} />
          )}
          <div className="flex flex-wrap gap-2">
            {!error && <button type="button" className="btn-primary btn-sm" onClick={() => { onUse(draft); setOpen(false); }}>{dv.yaumiyya.useDraft}</button>}
            <button type="button" className="btn-ghost btn-sm" onClick={() => setOpen(false)}>{dv.talkingPoints.cancel}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// A talking-point row. `authorName`/`authorRole` are the snapshot carried by an
// EXISTING (seeded) point, so a point whose author is no longer in the attendee
// list (e.g. a migration-era SG-authored point, or a member later un-marked
// attended) still renders under its real author and can never be silently
// re-attributed — it's edit-or-delete only.
type TpRow = { councilorId: string; text: string; authorName?: string; authorRole?: string };

// Talking points are stored as one bullet per line. Strip any existing marker,
// drop blank lines, and re-prefix every non-empty line with "• " so what we save
// (and feed the AI) is clean bullet text regardless of how the user typed it.
function normalizeBullets(text: string): string {
  return text
    .split('\n')
    .map((l) => l.replace(/^\s*[•●▪]?\s*/, '').trimEnd())
    .filter((l) => l.trim().length > 0)
    .map((l) => `• ${l}`)
    .join('\n');
}

function TalkingPointsEditor({ meetingId, itemId, attendees }: {
  meetingId: string; itemId: string;
  attendees: Array<{ id: string; nameDv?: string | null; name: string; role: string; designationDv?: string | null }>;
}): JSX.Element {
  const qc = useQueryClient();
  const { data: points, isSuccess } = useQuery({
    queryKey: ['talkingPoints', meetingId, itemId],
    queryFn: () => talkingPointsApi.list(meetingId, itemId),
  });
  // Local editable rows. Seed from server points once, on the first successful
  // query settle (even if points is empty) — later refetches (e.g. after a
  // save's cache invalidation) must never re-seed and clobber in-progress edits.
  const [rows, setRows] = useState<TpRow[]>([]);
  const seeded = useRef(false);
  // Last-saved normalized text per councilor — lets the debounced autosave skip
  // rows that haven't actually changed.
  const savedTexts = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    if (seeded.current || !isSuccess) return;
    seeded.current = true;
    const seededRows = (points ?? []).map((p) => ({ councilorId: p.authorId, text: p.text, authorName: p.authorName, authorRole: p.authorRole }));
    setRows(seededRows);
    savedTexts.current = new Map(seededRows.map((r) => [r.councilorId, r.text]));
  }, [isSuccess, points]);

  const save = useMutation({
    mutationFn: ({ councilorId, text }: { councilorId: string; text: string }) =>
      talkingPointsApi.upsert(meetingId, itemId, councilorId, text),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['talkingPoints', meetingId, itemId] }),
  });

  // Autosave, same as the rest of the yaumiyya editor: 1s after edits settle,
  // persist every attributed row whose normalized text changed. Skipped until
  // the first real edit so seeding never triggers a write.
  const touchedTp = useRef(false);
  useEffect(() => {
    if (!touchedTp.current) return;
    const t = setTimeout(() => {
      for (const r of rows) {
        if (!r.councilorId) continue;
        const text = normalizeBullets(r.text);
        if (savedTexts.current.get(r.councilorId) !== text) {
          savedTexts.current.set(r.councilorId, text);
          save.mutate({ councilorId: r.councilorId, text });
        }
      }
    }, 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const used = new Set(rows.map((r) => r.councilorId).filter(Boolean));
  const available = attendees.filter((m) => !used.has(m.id));

  function setRow(i: number, patch: Partial<TpRow>): void {
    touchedTp.current = true;
    setRows((prev) => prev.map((r, ri) => (ri === i ? { ...r, ...patch } : r)));
  }
  function removeRow(i: number): void {
    const r = rows[i];
    setRows((prev) => prev.filter((_, ri) => ri !== i));
    if (r?.councilorId) { savedTexts.current.delete(r.councilorId); save.mutate({ councilorId: r.councilorId, text: '' }); } // empty text deletes
  }
  // Enter (without Shift) starts a new bullet instead of a bare newline.
  function bulletKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>, i: number): void {
    if (e.key !== 'Enter' || e.shiftKey) return;
    e.preventDefault();
    const ta = e.currentTarget;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const v = ta.value;
    setRow(i, { text: `${v.slice(0, start)}\n• ${v.slice(end)}` });
    const caret = start + 3; // '\n• '.length
    requestAnimationFrame(() => { try { ta.selectionStart = ta.selectionEnd = caret; } catch { /* detached */ } });
  }

  return (
    <div className="space-y-2">
      <p className="field-label">{dv.yaumiyya.membersSpoke}</p>
      {rows.map((r, i) => {
        const picked = attendees.find((m) => m.id === r.councilorId);
        // Any row that already has an author (picked attendee OR a seeded point
        // whose author isn't in the attendee list) shows a fixed name label — the
        // reassignable picker is ONLY for a brand-new, not-yet-attributed row.
        const label = picked
          ? `${picked.nameDv ?? picked.name} (${picked.designationDv ?? (dv.roles as Record<string, string>)[picked.role]})`
          : r.authorName ? `${r.authorName}${r.authorRole ? ` (${r.authorRole})` : ''}` : null;
        return (
          <div key={i} className="space-y-2 rounded-2xl bg-[hsl(var(--surface-container-low))] p-3">
            <div className="flex items-center justify-between gap-2">
              {r.councilorId ? (
                <span className="flex min-w-0 items-center gap-2 text-sm font-medium">
                  <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
                    <Icon name="record_voice_over" size={18} />
                  </span>
                  <span className="truncate">{label ?? '—'}</span>
                </span>
              ) : (
                <Dropdown
                  className="min-w-0 flex-1"
                  placeholder={dv.yaumiyya.pickCouncilor}
                  options={available.map((m) => ({ value: m.id, label: m.nameDv ?? m.name }))}
                  onChange={(id) => setRow(i, { councilorId: id })}
                />
              )}
              <button type="button" className="btn-danger btn-sm" onClick={() => removeRow(i)}>
                <Icon name="delete" size={16} />
              </button>
            </div>
            {/* The point field only appears once a member is attributed — a
                talking point must belong to a councilor. */}
            {r.councilorId && (
              <AutoGrowTextarea
                className="input min-h-[3.5rem]" placeholder={dv.yaumiyya.speakerPoint} value={r.text}
                // Render the "•" marker from a UI font (a clean, mid-line round
                // dot) — Faruma draws it tiny/low like a fullstop. Thaana glyphs
                // still fall through to Faruma since UI fonts lack them.
                style={{ fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, 'Faruma', 'Noto Sans Thaana', sans-serif" }}
                onChange={(e) => setRow(i, { text: e.target.value })}
                onKeyDown={(e) => bulletKeyDown(e, i)}
              />
            )}
          </div>
        );
      })}
      <button type="button" className="btn-secondary btn-sm" disabled={available.length === 0}
        onClick={() => setRows((prev) => [...prev, { councilorId: '', text: '• ' }])}>
        <Icon name="add" size={16} /> {dv.yaumiyya.addSpeaker}
      </button>
    </div>
  );
}

export function YaumiyyaEditor(): JSX.Element {
  const { meetingId } = useParams({ from: '/meetings/$meetingId/yaumiyya' });
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['yaumiyya', meetingId] });

  // After submit, fast-poll until the sign job creates signDocId, then hand off
  // to WapSign. openingSign guards against a double redirect.
  const [awaitingSign, setAwaitingSign] = useState(false);
  const [openingSign, setOpeningSign] = useState(false);
  const [signError, setSignError] = useState(false);
  const [genericError, setGenericError] = useState(false);
  // Bottom-bar coordination: only ONE of {recording bar, now-playing bar} can
  // be up at a time. 'record' shows RecordingBar; { play: id } shows
  // NowPlayingBar for that recording; null shows neither. Starting one always
  // replaces whatever the other was doing (see the two setters below).
  const [activeBar, setActiveBar] = useState<'record' | { play: string } | null>(null);
  // True once we've actually observed PENDING_APPROVAL during this awaiting
  // session. Only a genuine PENDING_APPROVAL→DRAFT revert (the sign job failing
  // because the sender has no WapSign account) should raise signError — NOT the
  // initial stale DRAFT cached from before submit propagated.
  const sawPending = useRef(false);

  const { data: meeting } = useQuery({ queryKey: ['meeting', meetingId], queryFn: () => meetingsApi.get(meetingId) });
  // The quorum setting (null = not configured) — drives the no-quorum mode.
  // Quorum is per body, so read the row for THIS meeting's body. The query is
  // gated on `meeting` and keyed on its body so it refetches if the body differs.
  const { data: org } = useQuery({
    queryKey: ['org', meeting?.body],
    queryFn: () => orgSettingsApi.get(meeting!.body),
    enabled: meeting != null,
  });
  const { data: yaumiyya } = useQuery({
    queryKey: ['yaumiyya', meetingId],
    queryFn: () => yaumiyyaApi.get(meetingId),
    refetchInterval: awaitingSign ? 2_000 : 15_000,
  });
  // Recordings list — same membership gate as the rest of the page (the page
  // already blocks non-body-members before this component renders any of its
  // data), so no new guard is needed here. Polls slowly so a PROCESSING row
  // flips to READY without a manual reload.
  const { data: recordings } = useQuery({
    queryKey: ['recordings', meetingId],
    queryFn: () => listRecordings(meetingId),
    refetchInterval: 10_000,
  });
  // Deleting a recording removes its row + on-disk audio server-side and frees a
  // slot against the per-meeting cap; refetch the list so the card updates.
  const deleteRec = useMutation({
    mutationFn: (id: string) => deleteRecording(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['recordings', meetingId] }),
  });

  // Open the WapSign editor for the signing document via SSO handoff.
  async function openInWapsign(signDocId: string): Promise<void> {
    if (signDocId.startsWith('demo-') || user?.isDemo) return;
    setOpeningSign(true);
    setSignError(false);
    setGenericError(false);
    try {
      const url = await wapsignApi.handoff(`/documents/${signDocId}/edit`);
      window.location.href = url;
    } catch (e) {
      // Only a real wapsign_account_not_found means "no account"; transient
      // failures (expired token, timeout) show a generic retry message instead.
      if ((e as { response?: { data?: { error?: string } } })?.response?.data?.error === 'wapsign_account_not_found') {
        setSignError(true);
      } else {
        setGenericError(true);
      }
      setOpeningSign(false);
    }
  }

  // Once submit has produced a signDocId, redirect the submitter into WapSign.
  useEffect(() => {
    if (!awaitingSign) return;
    const sid = yaumiyya?.signDocId;
    if (sid) {
      setAwaitingSign(false);
      void openInWapsign(sid);
    } else if (yaumiyya?.status === 'PENDING_APPROVAL') {
      // Submit landed — now watch for either signDocId or a real revert.
      sawPending.current = true;
    } else if (yaumiyya?.status === 'DRAFT' && sawPending.current) {
      // Genuine revert (PENDING_APPROVAL→DRAFT): the sign job failed because the
      // sender isn't on WapSign. The initial stale DRAFT is ignored above.
      setAwaitingSign(false);
      setSignError(true);
    }
  }, [awaitingSign, yaumiyya?.signDocId, yaumiyya?.status]);

  // All role gating is keyed on the meeting's body (defaults to COUNCIL until
  // the meeting loads — the early-return guard below blocks render anyway).
  const body = meeting?.body ?? 'COUNCIL';
  const isWriter = !!user && writerRoles(body).includes(user.role);
  // Acting chair (succession-aware) — resolved server-side, matches the backend
  // requireActingPresident gate.
  const isPresident = !!user && !!meeting && meeting.actingChairId === user.id;
  const status = yaumiyya?.status ?? 'DRAFT';
  const editable = isWriter && (!yaumiyya || status === 'DRAFT');

  const { data: members } = useQuery({
    queryKey: ['council-members', body],
    queryFn: () => yaumiyyaApi.councilMembers(body),
    enabled: editable,
  });

  // Local editable state.
  const [details, setDetails] = useState<Details>(EMPTY_DETAILS);
  const [attendedIds, setAttendedIds] = useState<Set<string>>(new Set());
  const [arrivalTimes, setArrivalTimes] = useState<Record<string, string>>({}); // member id -> arrival time HH:MM
  const [attendanceModes, setAttendanceModes] = useState<Record<string, AttendanceMode>>({}); // member id -> PHYSICAL | ONLINE
  const [absentReasons, setAbsentReasons] = useState<Record<string, string>>({}); // member id -> reason
  const [secretariatIds, setSecretariatIds] = useState<Set<string>>(new Set());
  const [secretariatExtra, setSecretariatExtra] = useState<Array<{ name: string; role: string }>>([]);
  const [observers, setObservers] = useState<ObserverRow[]>([]);
  const [notes, setNotes] = useState<NotesMap>({});
  // Editor dirty flag: flips true on the first user edit. While false we keep
  // local state synced to the server snapshot; once true we stop, so a poll or
  // autosave refetch can never clobber in-progress edits.
  const touched = useRef(false);
  const hydratedSig = useRef<string | null>(null);

  // Hydrate local state from the loaded yaumiyya. Re-runs whenever the server
  // snapshot changes (keyed on a content signature) so a STALE snapshot served
  // on client-side navigation is replaced as soon as the fresh fetch lands —
  // previously a one-shot guard locked in the stale data until a full reload.
  // details/notes/observers don't need the member list; the attendance id-sets
  // do (to map stored names back to member ids), so they fill in once members
  // load. Read-only mode renders attendance straight from the yaumiyya, so it
  // never depends on members.
  useEffect(() => {
    if (!yaumiyya || touched.current) return;
    const sig = JSON.stringify({
      s: yaumiyya.status,
      d: yaumiyya.details ?? {},
      n: yaumiyya.notes ?? {},
      a: yaumiyya.attendance ?? {},
      m: !!members,
    });
    if (sig === hydratedSig.current) return;
    setDetails(yaumiyya.details ?? {});
    setNotes(normalizeNotes(yaumiyya.notes ?? {}));
    setObservers(yaumiyya.attendance.observers ?? []);
    if (members) {
      // Map a stored attendance name back to a member id. Existing yaumiyyat were
      // saved with the English `name`; new ones save the Dhivehi `nameDv`. Match
      // both so old records still hydrate after the switch to Dhivehi names.
      const byName = new Map<string, string>();
      for (const m of members) {
        byName.set(m.name, m.id);
        if (m.nameDv?.trim()) byName.set(m.nameDv.trim(), m.id);
      }
      const att = new Set<string>();
      const times: Record<string, string> = {};
      const modes: Record<string, AttendanceMode> = {};
      for (const a of yaumiyya.attendance.attended) {
        const id = byName.get(a.name);
        if (id) { att.add(id); if (a.time) times[id] = a.time; if (a.mode) modes[id] = a.mode; }
      }
      setAttendedIds(att);
      setArrivalTimes(times);
      setAttendanceModes(modes);
      const reasons: Record<string, string> = {};
      for (const a of yaumiyya.attendance.absent) { const id = byName.get(a.name); if (id && a.reason) reasons[id] = a.reason; }
      setAbsentReasons(reasons);
      const secCandidateIds = new Set(members.filter((m) => SECRETARIAT_ROLES.has(m.role)).map((m) => m.id));
      const sec = new Set<string>();
      const secExtra: Array<{ name: string; role: string }> = [];
      for (const s of yaumiyya.attendance.secretariat) {
        const id = byName.get(s.name);
        if (id && secCandidateIds.has(id)) sec.add(id);
        else secExtra.push({ name: s.name, role: s.role });
      }
      setSecretariatIds(sec);
      setSecretariatExtra(secExtra);
    }
    hydratedSig.current = sig;
  }, [yaumiyya, members]);

  const allMembers = useMemo(() => members ?? [], [members]);
  // Attendance pickers list ONLY this body's voting members (president/vice/member).
  const votingRoles = useMemo(() => new Set<string>(electedRoles(body)), [body]);
  const votingMembers = useMemo(() => allMembers.filter((m) => votingRoles.has(m.role)), [allMembers, votingRoles]);
  // Secretariat picker lists the SG + bureau staff.
  const secretariatCandidates = useMemo(() => allMembers.filter((m) => SECRETARIAT_ROLES.has(m.role)), [allMembers]);
  const attendedMembers = useMemo(
    () => votingMembers.filter((m) => attendedIds.has(m.id)),
    [votingMembers, attendedIds],
  );
  // Talking-point speakers: attended voting members PLUS attended secretariat
  // (the SG / bureau) — they speak in the meeting too, so they can be attributed
  // a talking point (the backend already allows bodyParticipantRoles). This is a
  // superset of attendedMembers and is used ONLY for the talking-points picker,
  // not for the vote table (only elected members vote).
  const attendedSpeakers = useMemo(
    () => [...attendedMembers, ...secretariatCandidates.filter((m) => secretariatIds.has(m.id))],
    [attendedMembers, secretariatCandidates, secretariatIds],
  );
  // Absent = voting members NOT marked attended.
  const absentMembers = useMemo(
    () => votingMembers.filter((m) => !attendedIds.has(m.id)),
    [votingMembers, attendedIds],
  );
  const attendedNames = attendedMembers.map((m) => displayName(m));

  // Quorum: a fixed minimum number of voting councilors. null = not configured
  // (no quorum enforced → always met). Computed live as attendance is toggled.
  // When the editor isn't editing (read-only) we trust the saved determination;
  // while editing we compute from the live attendance against the org setting.
  const quorum = org?.quorum ?? null;
  const quorumMet = !editable
    ? (yaumiyya?.quorumMet ?? true)
    : (quorum == null || attendedMembers.length >= quorum);

  const save = useMutation({
    mutationFn: () => {
      const attendance: Attendance = {
        attended: attendedMembers.map((m) => ({ name: displayName(m), role: m.designationDv ?? dv.roles[m.role], time: arrivalTimes[m.id]?.trim() || undefined, mode: attendanceModes[m.id] ?? 'PHYSICAL' })),
        absent: absentMembers.map((m) => ({ name: displayName(m), role: m.designationDv ?? dv.roles[m.role], reason: absentReasons[m.id]?.trim() || undefined })),
        secretariat: [
          ...secretariatCandidates.filter((m) => secretariatIds.has(m.id)).map((m) => ({ name: displayName(m), role: m.designationDv ?? dv.roles[m.role] })),
          ...secretariatExtra.filter((r) => r.name.trim()).map((r) => ({ name: r.name.trim(), role: r.role.trim() })),
        ],
        observers: observers.filter((o) => o.name.trim()).map((o) => ({
          name: o.name.trim(), address: o.address?.trim() || undefined, role: o.role?.trim() || undefined,
        })),
      };
      // Drop blank note fields so empty objects don't bloat the payload. Votes
      // are pruned to currently-attended voting members so a member marked absent
      // after voting never leaks into the PDF lists.
      const attendedSet = new Set(attendedMembers.map((m) => m.id));
      const cleanNotes: NotesMap = {};
      for (const [id, n] of Object.entries(notes)) {
        const entry: NoteEntry = {};
        if (n.discussion?.trim()) entry.discussion = n.discussion;
        const cleanProposals: Proposal[] = [];
        for (const p of n.proposals ?? []) {
          const cp: Proposal = {};
          if (p.text?.trim()) cp.text = p.text;
          if (p.proposedBy?.trim()) cp.proposedBy = p.proposedBy;
          if (p.secondedBy?.trim()) cp.secondedBy = p.secondedBy;
          if (p.decision?.trim()) cp.decision = p.decision;
          // Prefer the per-member vote map. Once `votes` exists it is the source
          // of truth (legacy arrays were dropped on first edit); prune to attended
          // members so a member marked absent after voting never leaks into the PDF.
          if (p.votes !== undefined) {
            const prunedVotes = p.votes.filter((v) => attendedSet.has(v.userId));
            if (prunedVotes.length) cp.votes = prunedVotes;
          } else {
            if (p.votesFor && p.votesFor.length) cp.votesFor = p.votesFor;
            if (p.votesAgainst && p.votesAgainst.length) cp.votesAgainst = p.votesAgainst;
          }
          if (Object.keys(cp).length) cleanProposals.push(cp);
        }
        if (cleanProposals.length) entry.proposals = cleanProposals;
        if (Object.keys(entry).length) cleanNotes[id] = entry;
      }
      const cleanDetails: Details = {};
      for (const [k, v] of Object.entries(details)) {
        if (typeof v === 'string' && v.trim()) cleanDetails[k as keyof Details] = v;
      }
      // A no-quorum meeting wasn't validly held: record NO per-item discussion/
      // voting (send notes: {}). Always send quorumMet so the determination is
      // persisted on the yaumiyya row.
      return yaumiyyaApi.save(meetingId, {
        attendance, notes: quorumMet ? cleanNotes : {}, details: cleanDetails, quorumMet,
      });
    },
    onSuccess: invalidate,
  });
  const submit = useMutation({
    mutationFn: () => yaumiyyaApi.submit(meetingId),
    onSuccess: () => { sawPending.current = false; setAwaitingSign(true); setSignError(false); invalidate(); },
  });
  const withdraw = useMutation({ mutationFn: () => yaumiyyaApi.withdraw(meetingId), onSuccess: () => { setAwaitingSign(false); invalidate(); } });
  const reopen = useMutation({ mutationFn: () => yaumiyyaApi.reopen(meetingId), onSuccess: () => { setAwaitingSign(false); invalidate(); } });

  // Autosave: debounce-persist the draft whenever an editable field changes.
  // `save.mutate` is stable across renders; we skip the first pass right after
  // hydration so loading the record doesn't trigger a write. Only runs while the
  // yaumiyya is an editable DRAFT. The manual Save button stays as an explicit flush.
  useEffect(() => {
    // Only autosave after a genuine user edit — hydration sets state with
    // `touched` still false, so loading/refetching never schedules a save.
    if (!editable || !touched.current) return;
    const t = setTimeout(() => save.mutate(), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable, details, attendedIds, arrivalTimes, attendanceModes, absentReasons, secretariatIds, secretariatExtra, observers, notes, quorumMet]);

  if (!meeting || !user) return <p className="p-4 text-sm text-muted-foreground">{dv.common.loading}</p>;

  // Approved items in the SAME order the agenda PDF numbers them (section first,
  // then orderIndex) — that's the order the meeting is actually run in.
  const approved: AgendaItemRow[] = sortByAgendaOrder(
    meeting.agendaItems.filter((i) => i.status === 'APPROVED'),
  );
  const doc = yaumiyya?.documents[0] ?? null;
  const signing = status === 'PENDING_APPROVAL';

  function setDetail(k: keyof Details, v: string): void {
    touched.current = true;
    setDetails((prev) => ({ ...prev, [k]: v }));
  }
  function toggle(set: React.Dispatch<React.SetStateAction<Set<string>>>, id: string): void {
    touched.current = true;
    set((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }
  function setNote(id: string, patch: Partial<NoteEntry>): void {
    touched.current = true;
    setNotes((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }
  // Proposal helpers. Each agenda item holds a list of ހުށަހެޅުން; the editor
  // always works through `proposals` (legacy notes were migrated on hydration).
  function proposalsOfItem(id: string): Proposal[] {
    return notes[id]?.proposals ?? [];
  }
  function setProposal(itemId: string, pIdx: number, patch: Partial<Proposal>): void {
    touched.current = true;
    setNotes((prev) => {
      const note = prev[itemId] ?? {};
      const proposals = [...(note.proposals ?? [])];
      proposals[pIdx] = { ...proposals[pIdx], ...patch };
      return { ...prev, [itemId]: { ...note, proposals } };
    });
  }
  function addProposal(itemId: string): void {
    touched.current = true;
    setNotes((prev) => {
      const note = prev[itemId] ?? {};
      return { ...prev, [itemId]: { ...note, proposals: [...(note.proposals ?? []), {}] } };
    });
  }
  function removeProposal(itemId: string, pIdx: number): void {
    touched.current = true;
    setNotes((prev) => {
      const note = prev[itemId] ?? {};
      const proposals = (note.proposals ?? []).filter((_, i) => i !== pIdx);
      return { ...prev, [itemId]: { ...note, proposals } };
    });
  }
  // Touch-aware wrappers for the inline list editors (absent reasons, secretariat
  // rows, observers) so any edit there also arms autosave + freezes hydration.
  const editAbsentReasons: typeof setAbsentReasons = (u) => { touched.current = true; setAbsentReasons(u); };
  const editArrivalTimes: typeof setArrivalTimes = (u) => { touched.current = true; setArrivalTimes(u); };
  const editAttendanceModes: typeof setAttendanceModes = (u) => { touched.current = true; setAttendanceModes(u); };
  const editSecretariatExtra: typeof setSecretariatExtra = (u) => { touched.current = true; setSecretariatExtra(u); };
  const editObservers: typeof setObservers = (u) => { touched.current = true; setObservers(u); };
  // Set (or clear, when choice === null) a single member's vote on a proposal.
  // The vote table is the source of truth, so writing here drops any legacy
  // votesFor/votesAgainst name arrays for that proposal to avoid double-counting.
  function setVote(itemId: string, pIdx: number, userId: string, name: string, role: string, choice: VoteChoice | null): void {
    touched.current = true;
    setNotes((prev) => {
      const note = prev[itemId] ?? {};
      const proposals = [...(note.proposals ?? [])];
      const cur = proposals[pIdx]?.votes ?? [];
      const rest = cur.filter((v) => v.userId !== userId);
      const next = choice ? [...rest, { userId, name, role, vote: choice }] : rest;
      const p: Proposal = { ...proposals[pIdx], votes: next };
      delete p.votesFor;
      delete p.votesAgainst;
      proposals[pIdx] = p;
      return { ...prev, [itemId]: { ...note, proposals } };
    });
  }
  // Look up a member's current vote on a proposal (votes map only; legacy arrays
  // render as an untouched empty table until the user sets a cell).
  function voteOf(itemId: string, pIdx: number, userId: string): VoteChoice | null {
    return notes[itemId]?.proposals?.[pIdx]?.votes?.find((v) => v.userId === userId)?.vote ?? null;
  }

  return (
    <div className="page space-y-6 lg:space-y-8">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <p className="status-label">{dv.yaumiyya.title}</p>
          <h1 className="text-3xl font-bold tracking-tight">
            {meeting.title}
          </h1>
          <p className="text-sm text-muted-foreground">
            <span dir="ltr">{formatDvDate(meeting.scheduledAt)} · {formatDvTime(meeting.scheduledAt)}</span>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Link
            to="/meetings/$meetingId"
            params={{ meetingId }}
            className="btn-secondary btn-sm"
          >
            <Icon name="arrow_forward" size={16} /> {dv.common.back}
          </Link>
        </div>
      </div>

      {/* Start-recording trigger now lives as a pill at the bottom of the
          recordings card below (replaces the old floating action button). */}
      {activeBar === 'record' && (
        <RecordingBar meetingId={meetingId} onClosed={() => setActiveBar(null)} />
      )}
      {activeBar !== null && activeBar !== 'record' && (
        <NowPlayingBar recordingId={activeBar.play} onClose={() => setActiveBar(null)} />
      )}

      {/* Status chip + number */}
      <div className="flex items-center gap-3">
        <span className={`pill ${
          status === 'APPROVED' ? 'pill-success'
            : status === 'PENDING_APPROVAL' ? 'pill-pending'
            : 'pill-active'
        }`}>
          {dv.yaumiyya.status[status]}
        </span>
        {yaumiyya?.yaumiyyaNumber != null && (
          <span className="text-sm text-muted-foreground tabular" dir="ltr">#{yaumiyya.yaumiyyaNumber}</span>
        )}
      </div>

      {/* === Meta (+ recordings side panel when present) === */}
      {(() => {
        const metaCard = (
          <section className="h-full space-y-4 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
            <h2 className="section-heading">{dv.yaumiyya.meta}</h2>
            {editable ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label={dv.yaumiyya.chair}>
                  <input list="member-names" className={inputCls} value={details.chair ?? ''} onChange={(e) => setDetail('chair', e.target.value)} />
                  <datalist id="member-names">
                    {votingMembers.map((m) => <option key={m.id} value={displayName(m)} />)}
                  </datalist>
                </Field>
                <Field label={dv.yaumiyya.startedTime}>
                  <input type="time" dir="ltr" className={inputCls} value={details.startedTime ?? ''} onChange={(e) => setDetail('startedTime', e.target.value)} />
                </Field>
                <Field label={dv.yaumiyya.interruptedTime}>
                  <input type="time" dir="ltr" className={inputCls} value={details.interruptedTime ?? ''} onChange={(e) => setDetail('interruptedTime', e.target.value)} />
                </Field>
                <Field label={dv.yaumiyya.resumedTime}>
                  <input type="time" dir="ltr" className={inputCls} value={details.resumedTime ?? ''} onChange={(e) => setDetail('resumedTime', e.target.value)} />
                </Field>
                <Field label={dv.yaumiyya.endedTime}>
                  <input type="time" dir="ltr" className={inputCls} value={details.endedTime ?? ''} onChange={(e) => setDetail('endedTime', e.target.value)} />
                </Field>
              </div>
            ) : (
              <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <div className="rounded-2xl bg-[hsl(var(--surface-container-low))] px-4 py-3">
                  <dt className="field-label">{dv.yaumiyya.chair}</dt>
                  <dd className="mt-0.5 font-medium">{yaumiyya?.details.chair || '—'}</dd>
                </div>
                <div className="rounded-2xl bg-[hsl(var(--surface-container-low))] px-4 py-3">
                  <dt className="field-label">{dv.yaumiyya.startedTime}</dt>
                  <dd className="mt-0.5 font-medium tabular" dir="ltr">{yaumiyya?.details.startedTime || '—'}</dd>
                </div>
                <div className="rounded-2xl bg-[hsl(var(--surface-container-low))] px-4 py-3">
                  <dt className="field-label">{dv.yaumiyya.endedTime}</dt>
                  <dd className="mt-0.5 font-medium tabular" dir="ltr">{yaumiyya?.details.endedTime || '—'}</dd>
                </div>
              </dl>
            )}
          </section>
        );

        const hasRecordings = !!recordings && recordings.length > 0;
        if (!isWriter && !hasRecordings) return metaCard;

        return (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 h-full">{metaCard}</div>
            {/* Recordings card — visible to anyone who can see this page (same
                membership gate as the rest of the editor). Clicking Play on a
                READY row takes over the bottom bar from any in-progress
                recording. Shown to writers even with zero recordings, since the
                start-recording pill lives at its bottom (replacing the old
                floating action button). h-full so it matches the meeting-info
                card's height on the shared lg row. */}
            <div className="lg:col-span-1 h-full">
              <RecordingsCard
                recordings={recordings ?? []}
                isWriter={isWriter}
                canRecord={activeBar === null}
                onPlay={(id) => setActiveBar({ play: id })}
                onRecord={() => setActiveBar('record')}
                onDelete={(id) => deleteRec.mutate(id)}
              />
            </div>
          </div>
        );
      })()}

      {/* === Attendance === */}
      <section className="space-y-5 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
        <h2 className="section-heading">{dv.yaumiyya.attendance}</h2>
        {editable ? (
          <>
            {/* Attended */}
            <div className="space-y-2">
              <h3 className="field-label">{dv.yaumiyya.attended}</h3>
              <div className="space-y-2">
                {votingMembers.map((m) => {
                  const on = attendedIds.has(m.id);
                  return (
                  <div key={m.id} className="flex flex-wrap items-center gap-2 rounded-2xl bg-[hsl(var(--surface-container-low))] px-3 py-2.5 text-sm">
                    <label className="flex min-w-0 flex-1 items-center gap-3">
                      <input type="checkbox" checked={on} onChange={() => toggle(setAttendedIds, m.id)} />
                      <span className={`grid size-9 shrink-0 place-items-center rounded-full ${on ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]' : 'bg-[hsl(var(--surface-high))] text-muted-foreground'}`}>
                        <Icon name="person" size={18} fill={on} />
                      </span>
                      <span className="min-w-0">{displayName(m)} <span className="text-muted-foreground">({m.designationDv ?? dv.roles[m.role]})</span></span>
                    </label>
                    {/* Attendance mode + arrival time — only for attended councilors; both feed the haaziree. */}
                    {on && (
                      <>
                        <Dropdown
                          className="w-32 shrink-0"
                          value={attendanceModes[m.id] ?? 'PHYSICAL'}
                          options={[
                            { value: 'PHYSICAL', label: dv.yaumiyya.modePhysical },
                            { value: 'ONLINE', label: dv.yaumiyya.modeOnline },
                          ]}
                          onChange={(v) => editAttendanceModes((prev) => ({ ...prev, [m.id]: v as AttendanceMode }))}
                        />
                        <input
                          type="time" dir="ltr"
                          aria-label={dv.yaumiyya.arrivalTime} title={dv.yaumiyya.arrivalTime}
                          className={`${inputCls} w-28 shrink-0`}
                          value={arrivalTimes[m.id] ?? ''}
                          onChange={(e) => editArrivalTimes((prev) => ({ ...prev, [m.id]: e.target.value }))}
                        />
                      </>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
            {/* Absent (auto = not attended) with a reason each */}
            {absentMembers.length > 0 && (
              <div className="space-y-2">
                <h3 className="field-label">{dv.yaumiyya.absent}</h3>
                {absentMembers.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 rounded-2xl bg-[hsl(var(--surface-container-low))] px-3 py-2.5 text-sm">
                    <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[hsl(var(--surface-high))] text-muted-foreground">
                      <Icon name="person_off" size={18} />
                    </span>
                    <span className="w-32 shrink-0 sm:w-40">{displayName(m)}</span>
                    <input
                      placeholder={dv.yaumiyya.absentReason}
                      className={inputCls}
                      value={absentReasons[m.id] ?? ''}
                      onChange={(e) => editAbsentReasons((prev) => ({ ...prev, [m.id]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            )}
            {/* Secretariat: SG checkboxes + free-add rows */}
            <div className="space-y-2">
              <h3 className="field-label">{dv.yaumiyya.secretariat}</h3>
              {secretariatCandidates.map((m) => {
                const on = secretariatIds.has(m.id);
                return (
                <label key={m.id} className="flex items-center gap-3 rounded-2xl bg-[hsl(var(--surface-container-low))] px-3 py-2.5 text-sm">
                  <input type="checkbox" checked={on} onChange={() => toggle(setSecretariatIds, m.id)} />
                  <span className={`grid size-9 shrink-0 place-items-center rounded-full ${on ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]' : 'bg-[hsl(var(--surface-high))] text-muted-foreground'}`}>
                    <Icon name="badge" size={18} fill={on} />
                  </span>
                  <span>{displayName(m)} <span className="text-muted-foreground">({m.designationDv ?? dv.roles[m.role]})</span></span>
                </label>
                );
              })}
              {secretariatExtra.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <input
                    placeholder={dv.yaumiyya.name} className={inputCls} value={r.name}
                    onChange={(e) => editSecretariatExtra((prev) => prev.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x))}
                  />
                  <input
                    placeholder={dv.yaumiyya.role} className={inputCls} value={r.role}
                    onChange={(e) => editSecretariatExtra((prev) => prev.map((x, xi) => xi === i ? { ...x, role: e.target.value } : x))}
                  />
                  <button type="button" className="btn-secondary btn-sm shrink-0"
                    onClick={() => editSecretariatExtra((prev) => prev.filter((_, xi) => xi !== i))}>
                    <Icon name="delete" size={16} /> {dv.yaumiyya.removeRow}
                  </button>
                </div>
              ))}
              <button type="button" className="btn-secondary btn-sm"
                onClick={() => editSecretariatExtra((prev) => [...prev, { name: '', role: '' }])}>
                <Icon name="add" size={16} /> {dv.yaumiyya.addRow}
              </button>
            </div>
            {/* Observers: dynamic add/remove */}
            <div className="space-y-2">
              <h3 className="field-label">{dv.yaumiyya.observers}</h3>
              {observers.map((o, i) => (
                <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-4 sm:items-center">
                  <input placeholder={dv.yaumiyya.name} className={inputCls} value={o.name}
                    onChange={(e) => editObservers((prev) => prev.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x))} />
                  <input placeholder={dv.yaumiyya.observerAddress} className={inputCls} value={o.address ?? ''}
                    onChange={(e) => editObservers((prev) => prev.map((x, xi) => xi === i ? { ...x, address: e.target.value } : x))} />
                  <input placeholder={dv.yaumiyya.observerRole} className={inputCls} value={o.role ?? ''}
                    onChange={(e) => editObservers((prev) => prev.map((x, xi) => xi === i ? { ...x, role: e.target.value } : x))} />
                  <button type="button" className="btn-secondary btn-sm"
                    onClick={() => editObservers((prev) => prev.filter((_, xi) => xi !== i))}>
                    <Icon name="delete" size={16} /> {dv.yaumiyya.removeRow}
                  </button>
                </div>
              ))}
              <button type="button" className="btn-secondary btn-sm"
                onClick={() => editObservers((prev) => [...prev, { name: '', address: '', role: '' }])}>
                <Icon name="add" size={16} /> {dv.yaumiyya.addRow}
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-3">
            <h3 className="field-label">{dv.yaumiyya.attended}</h3>
            {(yaumiyya?.attendance.attended ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{dv.common.none}</p>
            ) : (
              <div className="rounded-2xl bg-[hsl(var(--surface-container-low))] divide-y divide-black/[0.05] overflow-hidden">
                {(yaumiyya?.attendance.attended ?? []).map((a, i) => (
                  <div key={i} className="flex items-center gap-4 px-4 py-3 transition-colors hover:bg-[hsl(var(--surface-high))]">
                    <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
                      <Icon name="person" size={22} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium truncate">{a.name}</p>
                      <p className="mt-0.5 text-sm text-muted-foreground truncate">
                        {a.role}{a.mode ? ` · ${a.mode === 'ONLINE' ? dv.yaumiyya.modeOnline : dv.yaumiyya.modePhysical}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Meeting opening — placed right after attendance. */}
      {editable && (
        <section className="rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
          <Field label={dv.yaumiyya.openingText}>
            <AutoGrowTextarea className={`${inputCls} min-h-[4.5rem]`} value={details.openingText ?? ''} onChange={(e) => setDetail('openingText', e.target.value)} />
          </Field>
        </section>
      )}

      {/* === Per-item discussion / votes (flat, document order). Hidden when the
          meeting didn't meet quorum — it wasn't validly held, so no discussion or
          voting is recorded; a notice is shown instead (attendance still stands). === */}
      {!quorumMet ? (
        <section className="flex items-start gap-4 rounded-3xl bg-[hsl(var(--warning-container))] p-5 text-sm lg:p-6">
          <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--warning-container))] text-[hsl(var(--warning))]">
            <Icon name="warning" size={22} />
          </span>
          <div className="min-w-0 space-y-1">
            <p className="font-medium text-[hsl(var(--warning))]">{dv.yaumiyya.quorumNotMet}</p>
            <p className="text-[hsl(var(--warning))]" dir="rtl">
              {dv.yaumiyya.quorumCounts(quorum ?? 0, attendedMembers.length)}
            </p>
          </div>
        </section>
      ) : (
      <section className="space-y-6">
        {approved.map((item, idx) => {
          const note = notes[item.id] ?? {};
          return (
            <div key={item.id} className="space-y-4 rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
              <div className="flex items-center gap-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] text-sm font-semibold tabular">{idx + 1}</span>
                <p className="min-w-0 font-medium">{item.title}</p>
              </div>
              {editable ? (
                <>
                  <TalkingPointsEditor
                    meetingId={meetingId}
                    itemId={item.id}
                    attendees={attendedSpeakers}
                  />
                  <GenerateDiscussion
                    meetingId={meetingId}
                    itemId={item.id}
                    onUse={(text) => setNote(item.id, { discussion: text })}
                  />
                  <Field label={dv.yaumiyya.discussion}>
                    <AutoGrowTextarea className={`${inputCls} min-h-[4.5rem]`} value={note.discussion ?? ''}
                      onChange={(e) => setNote(item.id, { discussion: e.target.value })} />
                  </Field>
                  <datalist id="attended-names">
                    {attendedNames.map((n) => <option key={n} value={n} />)}
                  </datalist>
                  {/* One card per ހުށަހެޅުން. An item may carry several; each is
                      proposed, voted and decided independently (both can pass). */}
                  {proposalsOfItem(item.id).map((p, pIdx) => (
                    <div key={pIdx} className="space-y-3 rounded-2xl bg-[hsl(var(--surface-container-low))] p-4">
                      <div className="flex items-center justify-between">
                        <p className="flex items-center gap-2 text-sm font-semibold">
                          <span className="grid size-8 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
                            <Icon name="how_to_vote" size={16} />
                          </span>
                          {dv.yaumiyya.proposal} {pIdx + 1}
                        </p>
                        <button type="button" className="btn-danger btn-sm"
                          onClick={() => removeProposal(item.id, pIdx)}>
                          <Icon name="delete" size={16} /> {dv.yaumiyya.removeProposal}
                        </button>
                      </div>
                      <Field label={dv.yaumiyya.proposalText}>
                        <AutoGrowTextarea className={`${inputCls} min-h-[3.5rem]`} value={p.text ?? ''}
                          onChange={(e) => setProposal(item.id, pIdx, { text: e.target.value })} />
                      </Field>
                      <Field label={dv.yaumiyya.proposedBy}>
                        <input list="attended-names" className={inputCls} value={p.proposedBy ?? ''}
                          onChange={(e) => setProposal(item.id, pIdx, { proposedBy: e.target.value })} />
                      </Field>
                      <div className="space-y-1">
                        <p className="field-label">{dv.yaumiyya.voteDetail}</p>
                        {attendedMembers.length === 0 ? (
                          <p className="text-sm text-muted-foreground">{dv.common.none}</p>
                        ) : (
                          <table className="w-full border-collapse text-sm">
                            <thead>
                              <tr className="border-b border-[hsl(var(--border))] text-muted-foreground">
                                <th className="py-1.5 text-start font-medium">{dv.yaumiyya.voteTableName}</th>
                                <th className="py-1.5 text-start font-medium">{dv.users.role}</th>
                                <th className="py-1.5 text-center font-medium">{dv.yaumiyya.fenna}</th>
                                <th className="py-1.5 text-center font-medium">{dv.yaumiyya.nufenna}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {attendedMembers.map((m) => {
                                const cur = voteOf(item.id, pIdx, m.id);
                                return (
                                  <tr key={m.id} className="border-b border-black/[0.05]">
                                    <td className="py-1.5">{displayName(m)}</td>
                                    <td className="py-1.5 text-muted-foreground">{m.designationDv ?? dv.roles[m.role] ?? m.role}</td>
                                    {(['FOR', 'AGAINST'] as const).map((choice) => (
                                      <td key={choice} className="py-1.5 text-center">
                                        <input
                                          type="radio"
                                          name={`vote-${item.id}-${pIdx}-${m.id}`}
                                          checked={cur === choice}
                                          aria-label={choice === 'FOR' ? dv.yaumiyya.fenna : dv.yaumiyya.nufenna}
                                          onClick={() => setVote(item.id, pIdx, m.id, displayName(m), m.designationDv ?? dv.roles[m.role] ?? m.role, cur === choice ? null : choice)}
                                          onChange={() => { /* handled in onClick to allow toggle-off */ }}
                                        />
                                      </td>
                                    ))}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        )}
                      </div>
                      <Field label={dv.yaumiyya.decision}>
                        <AutoGrowTextarea className={`${inputCls} min-h-[3.5rem]`} value={p.decision ?? ''}
                          onChange={(e) => setProposal(item.id, pIdx, { decision: e.target.value })} />
                      </Field>
                    </div>
                  ))}
                  <button type="button" className="btn-primary btn-sm"
                    onClick={() => addProposal(item.id)}>
                    <Icon name="add" size={16} /> {dv.yaumiyya.addProposal}
                  </button>
                </>
              ) : (() => {
                // Read-only view shows the full note: discussion, then each
                // proposal (proposer → vote table → decision). Labels «ހުށަހެޅުން N»
                // only when the item carries more than one.
                const proposals = note.proposals ?? [];
                const proposalHasContent = (p: Proposal): boolean => !!(p.text?.trim() || p.proposedBy?.trim()
                  || p.secondedBy?.trim() || p.decision?.trim()
                  || p.votes?.length || p.votesFor?.length || p.votesAgainst?.length);
                const hasAny = !!(note.discussion?.trim() || proposals.some(proposalHasContent));
                if (!hasAny) return <p className="text-sm text-muted-foreground">{dv.common.none}</p>;
                const multi = proposals.length > 1;
                return (
                  <div className="space-y-3 text-sm">
                    {note.discussion?.trim() && <p className="whitespace-pre-wrap text-muted-foreground">{note.discussion}</p>}
                    {proposals.map((p, pIdx) => {
                      if (!proposalHasContent(p)) return null;
                      const votes = p.votes ?? [];
                      const rows = votes.length === 0
                        ? [
                            ...(p.votesFor ?? []).map((n) => ({ userId: n, name: n, role: undefined as string | undefined, vote: 'FOR' as const })),
                            ...(p.votesAgainst ?? []).map((n) => ({ userId: n, name: n, role: undefined as string | undefined, vote: 'AGAINST' as const })),
                          ]
                        : votes;
                      return (
                        <div key={pIdx} className="space-y-2 border-t border-black/[0.05] pt-2 first:border-0 first:pt-0">
                          {(p.text?.trim() || multi) && (
                            <p className="font-semibold underline">
                              {dv.yaumiyya.proposal} {pIdx + 1}{p.text?.trim() ? `: ${p.text}` : ''}
                            </p>
                          )}
                          {p.proposedBy?.trim() && <p><span className="font-medium">{dv.yaumiyya.proposedBy}:</span> {p.proposedBy}</p>}
                          {p.secondedBy?.trim() && <p><span className="font-medium">{dv.yaumiyya.secondedBy}:</span> {p.secondedBy}</p>}
                          {rows.length > 0 && (
                            <table className="w-full border-collapse text-sm">
                              <thead>
                                <tr className="border-b border-[hsl(var(--border))] text-muted-foreground">
                                  <th className="py-1.5 text-start font-medium">{dv.yaumiyya.voteTableName}</th>
                                  <th className="py-1.5 text-start font-medium">{dv.users.role}</th>
                                  <th className="py-1.5 text-center font-medium">{dv.yaumiyya.fenna}</th>
                                  <th className="py-1.5 text-center font-medium">{dv.yaumiyya.nufenna}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map((v) => (
                                  <tr key={v.userId} className="border-b border-black/[0.05]">
                                    <td className="py-1.5">{v.name}</td>
                                    <td className="py-1.5 text-muted-foreground">{v.role ?? ''}</td>
                                    <td className="py-1.5 text-center">{v.vote === 'FOR' ? '✓' : ''}</td>
                                    <td className="py-1.5 text-center">{v.vote === 'AGAINST' ? '✓' : ''}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          {p.decision?.trim() && <p><span className="font-medium">{dv.yaumiyya.decision}:</span> {p.decision}</p>}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          );
        })}
      </section>
      )}

      {/* Meeting closing — placed last, as it ends the meeting. */}
      {editable && (
        <section className="rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6">
          <Field label={dv.yaumiyya.closingText}>
            <AutoGrowTextarea className={`${inputCls} min-h-[4.5rem]`} value={details.closingText ?? ''} onChange={(e) => setDetail('closingText', e.target.value)} />
          </Field>
        </section>
      )}

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {editable && (
          <>
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="btn-primary"
            >
              <Icon name="save" size={18} /> {dv.common.save}
            </button>
            <span className="self-center text-xs text-muted-foreground" aria-live="polite">
              {save.isPending ? dv.yaumiyya.saving : save.isSuccess ? dv.yaumiyya.saved : ''}
            </span>
            {yaumiyya && status === 'DRAFT' && (
              <button
                onClick={() => { if (window.confirm(dv.yaumiyya.submitConfirm)) submit.mutate(); }}
                disabled={submit.isPending}
                className="btn-secondary"
              >
                <Icon name="send" size={18} /> {dv.yaumiyya.submit}
              </button>
            )}
          </>
        )}
        {/* Signing-in-progress: open in WapSign + withdraw. */}
        {signing && yaumiyya?.signDocId && !user?.isDemo && (
          <button
            onClick={() => void openInWapsign(yaumiyya.signDocId!)}
            disabled={openingSign}
            className="btn-primary"
          >
            <Icon name="open_in_new" size={18} /> {dv.yaumiyya.openInWapsign}
          </button>
        )}
        {isWriter && signing && (
          <button
            onClick={() => withdraw.mutate()}
            disabled={withdraw.isPending}
            className="btn-secondary"
          >
            <Icon name="undo" size={18} /> {dv.yaumiyya.withdraw}
          </button>
        )}
        {/* Reopen for edit + resend — from PENDING_APPROVAL or APPROVED
            (WRITERS + PRESIDENT). Returns the yaumiyya to DRAFT (editable). */}
        {(isWriter || isPresident) && (status === 'PENDING_APPROVAL' || status === 'APPROVED') && (
          <button
            onClick={() => { if (window.confirm(dv.yaumiyya.reopenConfirm)) reopen.mutate(); }}
            disabled={reopen.isPending}
            className="btn-secondary"
          >
            <Icon name="lock_open" size={18} /> {dv.yaumiyya.reopen}
          </button>
        )}
      </div>

      {/* Signing status banner */}
      {signing && (
        <section className="flex items-start gap-4 rounded-3xl bg-[hsl(var(--warning-container))] p-5 text-sm lg:p-6">
          <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--warning-container))] text-[hsl(var(--warning))]">
            <Icon name="hourglass_top" size={22} />
          </span>
          <div className="min-w-0 space-y-1">
            <p className="font-medium text-[hsl(var(--warning))]">{dv.yaumiyya.signingInProgress}</p>
            {!yaumiyya?.signDocId && (
              <p className="text-[hsl(var(--warning))]">{dv.yaumiyya.preparingSigning}</p>
            )}
          </div>
        </section>
      )}
      {(awaitingSign || openingSign) && (
        <p className="text-sm text-muted-foreground">{dv.yaumiyya.preparingSigning}</p>
      )}
      {signError && (
        <p className="text-sm text-destructive">{dv.yaumiyya.wapsignNoAccount}</p>
      )}
      {genericError && (
        <p className="text-sm text-destructive">{dv.common.error}</p>
      )}

      {/* Document */}
      {doc && (
        <section className="space-y-3">
          <h2 className="section-heading">{dv.yaumiyya.document}</h2>
          <div className="flex flex-wrap items-center gap-4 rounded-3xl bg-[hsl(var(--chrome))] p-4 text-sm lg:p-5">
            <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
              <Icon name="description" size={22} />
            </span>
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              {status === 'APPROVED' ? (
                <span className="pill pill-success">{dv.yaumiyya.signed}</span>
              ) : doc.finalized && (
                <span className="pill pill-success">{dv.agenda.finalVersion}</span>
              )}
              <span className="text-muted-foreground tabular" dir="ltr">
                {formatDvDate(doc.generatedAt)} {formatDvTime(doc.generatedAt)}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {/* Preview = download the latest yaumiyya PDF (documents[0] is the
                  newest; older versions are pruned by the single-document model). */}
              <button
                onClick={() => void downloadAuthed(yaumiyyaApi.documentUrl(doc.id), `yaumiyya-${meeting.year}-${meeting.sequence}.pdf`)}
                className="btn-secondary btn-sm"
              >
                <Icon name="visibility" size={16} /> {dv.yaumiyya.preview}
              </button>
            </div>
          </div>
        </section>
      )}

      {!yaumiyya && !editable && (
        <p className="text-sm text-muted-foreground">{dv.yaumiyya.notYet}</p>
      )}
    </div>
  );
}
